/**
 * <product-question-list>
 *
 * Client-side pagination for the answered Q&A list. The entries live in a metafield, which
 * Liquid's paginate tag cannot page, so every item renders server-side and this component
 * shows data-page-size at a time. The nav mirrors snippets/pagination.liquid (traditional
 * style) class-for-class so it inherits the theme's pagination styling — buttons instead of
 * links, since pages are client-side state rather than URLs.
 *
 * Data attributes (all rendered by the Liquid snippet):
 *   data-page-size       items per page
 *   data-label-nav       aria-label for the nav
 *   data-label-prev      previous-arrow label
 *   data-label-next      next-arrow label
 *   data-label-position  mobile "Page x / y" template with [current] and [total] tokens
 *
 * Icons come from the <template data-qa-icons> child, rendered by Liquid.
 */
class ProductQuestionList extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.embark-qa-list > li'));
    this.pageSize = parseInt(this.dataset.pageSize, 10) || 5;
    this.pages = Math.ceil(this.items.length / this.pageSize);
    if (this.pages < 2) return;

    const icons = this.querySelector('template[data-qa-icons]');
    this.prevIcon = icons ? icons.content.querySelector('[data-prev]').innerHTML : '';
    this.nextIcon = icons ? icons.content.querySelector('[data-next]').innerHTML : '';

    this.page = 1;
    this.nav = document.createElement('nav');
    this.nav.setAttribute('aria-label', this.dataset.labelNav || 'Pagination');
    this.append(this.nav);
    this.nav.addEventListener('click', this.handleClick.bind(this));
    this.render();
  }

  handleClick(event) {
    const button = event.target.closest('button[data-page]');
    if (!button || button.disabled || this.animating) return;

    const previous = this.page;
    const target = button.dataset.page;
    if (target === 'prev') this.page -= 1;
    else if (target === 'next') this.page += 1;
    else this.page = parseInt(target, 10);

    this.page = Math.min(Math.max(this.page, 1), this.pages);
    if (this.page === previous) return;

    this.transitionRender();
  }

  /** Fade the list out, swap the page, then reveal items top-down. Instant under reduced motion. */
  transitionRender() {
    const list = this.querySelector('.embark-qa-list');
    if (!list || !list.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.render();
      if (list) this.scrollListIntoView(list);
      return;
    }

    this.animating = true;
    list
      .animate({ opacity: [1, 0] }, { duration: 150, easing: 'ease' })
      .finished.then(() => {
        this.render();
        this.scrollListIntoView(list);

        // fill: 'backwards' holds each item invisible through its delay, so the page
        // arrives empty and the questions appear one after another from the top.
        const reveals = this.items
          .filter((item) => !item.hidden)
          .map((item, index) =>
            item.animate(
              { opacity: [0, 1], transform: ['translateY(0.5rem)', 'none'] },
              { duration: 200, delay: index * 70, easing: 'ease', fill: 'backwards' }
            )
          );
        const lastReveal = reveals[reveals.length - 1];
        return lastReveal && lastReveal.finished;
      })
      .catch(() => this.render())
      .finally(() => {
        this.animating = false;
      });
  }

  /**
   * On small screens the nav sits below a tall list, so a page change swaps in new items far
   * above the viewport. Scroll the list top back into view, offset by the live bottom edge of
   * the (sticky) header — the same compensation DetailsDisclosure.keepInViewport uses: a
   * sticky header's bottom is its rendered height, a scrolled-away static header clamps to 0.
   */
  scrollListIntoView(list) {
    if (!window.matchMedia('(max-width: 767px)').matches) return;

    const storeHeader = document.querySelector('store-header');
    const headerOffset = Math.max(storeHeader ? storeHeader.getBoundingClientRect().bottom : 0, 0);
    const listTop = list.getBoundingClientRect().top;
    if (listTop >= headerOffset) return;

    window.scrollTo({
      top: listTop + window.scrollY - headerOffset - 16,
      behavior: 'smooth'
    });
  }

  /** Page numbers with ellipsis gaps: first, last, and current's neighbours — like paginate.parts */
  parts() {
    const parts = [];
    let last = 0;
    for (let p = 1; p <= this.pages; p += 1) {
      if (p === 1 || p === this.pages || Math.abs(p - this.page) <= 1) {
        if (p - last > 1) parts.push('gap');
        parts.push(p);
        last = p;
      }
    }
    return parts;
  }

  render() {
    const start = (this.page - 1) * this.pageSize;
    this.items.forEach((item, index) => {
      item.hidden = index < start || index >= start + this.pageSize;
    });

    const position = (this.dataset.labelPosition || 'Page [current] / [total]')
      .replace('[current]', this.page)
      .replace('[total]', this.pages);

    const numbers = this.parts().map((part) => {
      if (part === 'gap') return '<li class="hidden md:block"><span class="block leading-none ml-2 mr-2">&hellip;</span></li>';
      if (part === this.page) return `<li class="hidden md:block"><span class="pagination__page-current font-bold block leading-none">${part}</span></li>`;
      return `<li class="hidden md:block"><button type="button" class="pagination__page-link text-current block leading-none" data-page="${part}">${part}</button></li>`;
    }).join('');

    const prevDisabled = this.page === 1 ? ' disabled aria-disabled="true"' : '';
    const nextDisabled = this.page === this.pages ? ' disabled aria-disabled="true"' : '';

    this.nav.innerHTML = `
      <ul class="pagination relative flex flex-wrap justify-center items-center justify-between w-full mx-auto">
        <li class="pagination__item pagination__item--arrow">
          <button type="button" class="pagination__arrow pagination__arrow--prev hover:lighten flex w-full text-current items-center has-ltr-icon" data-page="prev"${prevDisabled}>
            <span class="pagination__arrow-icon" aria-hidden="true">${this.prevIcon}</span>
            <span class="pagination__prev-label mis-2">${this.dataset.labelPrev || 'Previous'}</span>
          </button>
        </li>
        <li class="pagination__item text-center md:hidden font-bold" aria-live="polite">${position}</li>
        ${numbers}
        <li class="pagination__item pagination__item--arrow">
          <button type="button" class="pagination__arrow pagination__arrow--next hover:lighten flex w-full justify-end text-current items-center has-ltr-icon" data-page="next"${nextDisabled}>
            <span class="pagination__next-label text-end">${this.dataset.labelNext || 'Next'}</span>
            <span class="pagination__arrow-icon" aria-hidden="true">${this.nextIcon}</span>
          </button>
        </li>
      </ul>`;
  }
}

if (!customElements.get('product-question-list')) {
  customElements.define('product-question-list', ProductQuestionList);
}
